import { useMemo, useState } from 'react'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import clsx from 'clsx'
import { formatCurrency, formatDate, formatRut } from '../../../../../../utils/formatter'
import isologoSII from '../../../../../../../assets/isologo_SII.png'
import imgDefaultBank from '../../../../../../../assets/iconos/iconCasa.png'
import iconVinculos from '../../../../../../../assets/iconosCardInformes/informe_9.png'
import { bankIcons, folioCreditoLabels } from '../../bancos/data'
import FiltrosProvisionAgenteAduana from './FiltrosProvisionAgenteAduana'

const BANCO_ORIGENES = new Set([6, 7])
const VINCULACION_ORIGENES = new Set([24, 26])

const getRowId = row =>
  row?.idPrincipal ??
  row?.id ??
  row?.libro?.idPrincipal ??
  row?.libro?.id ??
  row?.din?.idPrincipal ??
  row?.din?.id
const isBankMovement = row =>
  [row?.origenTabla, row?.tabla, row?.tablaPrincipal]
    .map(Number)
    .some(origin => BANCO_ORIGENES.has(origin))
const getRowAmount = row => {
  if (Number(row?.code) === 914) return Number(row?.montoIvaRetenido ?? 0)
  return Number(
    row?.montoTotal ??
      row?.montoAbono ??
      row?.montoCargo ??
      row?.monto ??
      row?.montoRelacionado ??
      row?.debe ??
      row?.haber ??
      0
  )
}

const getRowMovementType = row => {
  const explicitType = String(row?.tipoMovimiento ?? row?.tipo ?? '').trim().toLowerCase()
  if (explicitType === 'cargo' || explicitType === 'abono') return explicitType

  const movementTable = [row?.tabla, row?.tablaPrincipal, row?.origenTabla]
    .map(Number)
    .find(value => value === 6 || value === 7)
  if (movementTable === 7) return 'cargo'
  if (movementTable === 6) return 'abono'

  const origenTabla = Number(row?.origenTabla)
  if (origenTabla === 24) return 'cargo'
  if (origenTabla === 26) return 'abono'

  const montoCargo = Number(row?.montoCargo ?? row?.debe ?? 0)
  const montoAbono = Number(row?.montoAbono ?? row?.haber ?? 0)
  if (montoCargo !== 0 && montoAbono === 0) return 'cargo'
  if (montoAbono !== 0 && montoCargo === 0) return 'abono'

  const status = normalizeStatus(row?.status)
  if (status.includes('PAGAR') || status.includes('PAGADO')) return 'cargo'
  if (status.includes('COBRAR') || status.includes('COBRADO')) return 'abono'

  return null
}

const getRowMovementAmount = (row, movementType) => {
  if (Number(row?.code) === 914) return Number(row?.montoIvaRetenido ?? 0)
  const directionalAmount = movementType === 'cargo'
    ? row?.montoCargo ?? row?.debe
    : row?.montoAbono ?? row?.haber
  return Number(directionalAmount ?? getRowAmount(row))
}

const normalizeBankName = value =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const canonicalBankName = value =>
  normalizeBankName(value)
    .replace(/\bde\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const getBankIcon = razonSocial => {
  if (!razonSocial) return imgDefaultBank
  if (bankIcons[razonSocial]) return bankIcons[razonSocial]

  const normalizedName = normalizeBankName(razonSocial)
  const canonicalName = canonicalBankName(razonSocial)
  const matchingBank = Object.keys(bankIcons).find(bankName => {
    const normalizedBank = normalizeBankName(bankName)
    const identifyingTokens = normalizedBank
      .split(/\W+/)
      .filter(token => token.length >= 3 && !['banco', 'chile'].includes(token))

    return (
      normalizedName === normalizedBank ||
      canonicalName === canonicalBankName(bankName) ||
      normalizedName.includes(normalizedBank) ||
      identifyingTokens.some(token => normalizedName.includes(token))
    )
  })

  return matchingBank ? bankIcons[matchingBank] : imgDefaultBank
}

const getDocumentFilterLabel = row => {
  const origenTabla = Number(row?.origenTabla)

  if (BANCO_ORIGENES.has(origenTabla)) {
    return String(row?.razonSocial ?? '').trim()
  }

  if (VINCULACION_ORIGENES.has(origenTabla)) {
    return String(
      folioCreditoLabels[Number(row?.folioCredito)] ?? row?.razonSocial ?? ''
    ).trim()
  }

  return String(row?.nombreFolio ?? '').trim()
}

const normalizeStatus = status =>
  String(status || '')
    .trim()
    .toUpperCase()

const statusBadgeClass = status => {
  const normalized = normalizeStatus(status)
  return clsx(
    'inline-flex items-center justify-center rounded-full px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider',
    {
      'bg-danger/10 text-danger':
        normalized.includes('PENDIENTE') ||
        normalized.includes('POR PAGAR') ||
        normalized.includes('POR COBRAR'),
      'bg-success/10 text-success':
        normalized.includes('PAGADO') ||
        normalized.includes('COBRADO') ||
        normalized.includes('CONCILIADO'),
      'bg-amber-100 text-amber-700': normalized.includes('PARCIAL'),
      'bg-border/40 text-muted': !normalized
    }
  )
}

const TablaProvisionAgenteAduana = ({
  documentosSii,
  selectedIds,
  onToggle,
  disabled,
  loading,
  referenceAmount,
  siiOnly = false
}) => {
  const today = new Date().toISOString().split('T')[0]
  const [filters, setFilters] = useState({
    fechaInicio: `${new Date().getFullYear()}-01-01`,
    fechaFin: today,
    documento: '',
    keyword: '',
    montosSimilares: false
  })

  const nonBankDocuments = useMemo(
    () => documentosSii.filter(row => !isBankMovement(row)),
    [documentosSii]
  )

  const rawSections = [
    {
      key: 'sii',
      title: 'Documentos y movimientos',
      rows: nonBankDocuments
    }
  ].filter(section => !siiOnly || section.key === 'sii')

  const normalize = value =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

  const documentOptions = useMemo(() => {
    const options = new Map()

    nonBankDocuments.forEach(row => {
      const documentLabel = getDocumentFilterLabel(row)
      const normalizedName = normalize(documentLabel)
      const value = normalizedName
      if (documentLabel && !options.has(value)) {
        options.set(value, { value, label: documentLabel })
      }
    })

    return [...options.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
    )
  }, [nonBankDocuments])

  const getDateKey = value => {
    if (!value) return ''
    const parsedDate = new Date(value)
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().split('T')[0]
  }

  const filterRows = (rows, sectionKey) => {
    const keyword = normalize(filters.keyword)
    const filtered = rows.filter(row => {
      const rowDate = getDateKey(row?.fecha)
      const matchesDate =
        (!filters.fechaInicio || !rowDate || rowDate >= filters.fechaInicio) &&
        (!filters.fechaFin || !rowDate || rowDate <= filters.fechaFin)
      const matchesDocument =
        sectionKey !== 'sii' ||
        !filters.documento ||
        normalize(getDocumentFilterLabel(row)) === filters.documento
      const searchable = normalize(
        [
          row?.razonSocial,
          row?.rut,
          row?.nombreCuenta,
          row?.nombreCuentaBanco,
          row?.codigoCuenta,
          row?.numeroCuentaBanco,
          row?.descripcion,
          row?.glosa,
          row?.nombreFolio,
          row?.numeroFolio,
          row?.folio
        ].join(' ')
      )
      return (
        matchesDate &&
        matchesDocument &&
        (!keyword || searchable.includes(keyword))
      )
    })

    if (!filters.montosSimilares) return filtered
    return [...filtered].sort(
      (a, b) =>
        Math.abs(getRowAmount(a) - Number(referenceAmount || 0)) -
        Math.abs(getRowAmount(b) - Number(referenceAmount || 0))
    )
  }

  const sections = rawSections.map(section => ({
    ...section,
    rows: filterRows(section.rows, section.key)
  }))

  const handleFilterChange = (key, value) => {
    setFilters(current => ({ ...current, [key]: value }))
  }

  const renderRows = section => {
    if (disabled) {
      return (
        <div className='p-8 text-center text-xs text-gray-500'>
          Selecciona primero un agente de aduana.
        </div>
      )
    }
    if (loading) {
      return <div className='p-8 text-center text-xs text-gray-500'>Cargando información…</div>
    }
    if (section.rows.length === 0) {
      return (
        <div className='p-8 text-center text-xs text-gray-500'>No hay registros disponibles.</div>
      )
    }

    return (
      <div className='overflow-auto'>
        <table className='w-full table-auto border-collapse text-[0.725rem] leading-tight'>
          <thead className='sticky top-0 z-10 bg-gray-50'>
            <tr>
              <th className='w-16 border-b border-gray-200 px-1 py-2' />
              {[
                'Fecha',
                'Razón Social',
                'Cuenta Corriente',
                'Descripción',
                'Cargo',
                'Abono',
                'Status',
                'Acciones'
              ].map(label => (
                <th
                  key={label}
                  className='border-b border-gray-200 px-1 py-2 text-center text-[0.6rem] font-bold uppercase text-gray-500'
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.rows.map(row => {
              const id = getRowId(row)
              const esMovimientoBanco = BANCO_ORIGENES.has(Number(row?.origenTabla))
              const esVinculacion = VINCULACION_ORIGENES.has(Number(row?.origenTabla))
              const movementType = getRowMovementType(row)
              const amount = getRowMovementAmount(row, movementType)
              const checked = (selectedIds[section.key] || []).some(
                selectedId => String(selectedId) === String(id)
              )
              return (
                <tr
                  key={`${section.key}-${id}`}
                  className='border-b border-gray-100 transition-colors hover:bg-gray-50'
                >
                  <td className='px-2 py-2 text-center align-middle'>
                    <div className='flex items-center justify-center gap-1'>
                      <input
                        type='checkbox'
                        checked={checked}
                        onChange={() => onToggle(row, section.key)}
                        className='rounded'
                      />
                      {section.key === 'sii' && (
                        esMovimientoBanco ? (
                          <img
                            src={getBankIcon(row?.razonSocial)}
                            alt={row?.razonSocial || 'Banco'}
                            className='h-7 w-7 rounded object-contain'
                          />
                        ) : esVinculacion ? (
                          <img
                            src={iconVinculos}
                            alt='Vinculación'
                            className='h-7 w-7 object-contain'
                          />
                        ) : (
                          <img src={isologoSII} alt='SII' className='h-7 w-auto' />
                        )
                      )}
                    </div>
                  </td>
                  <td className='px-2 py-2 text-center align-middle'>
                    <span className='font-bold text-[0.65rem]'>
                      {row.fecha ? formatDate(row.fecha) : '-'}
                    </span>
                  </td>
                  <td className='px-2 py-2 text-center align-middle'>
                    <div className='font-bold text-[0.65rem]'>
                      {esVinculacion
                        ? folioCreditoLabels[Number(row?.folioCredito)] || row.razonSocial || '-'
                        : row.razonSocial || '-'}
                    </div>
                    <div className='text-[0.705rem] font-medium text-gray-400'>
                      {row.rut ? formatRut(row.rut) : ''}
                    </div>
                  </td>
                  <td className='px-2 py-2 text-center align-middle'>
                    <div className='font-bold text-[0.65rem]'>
                      {row.nombreCuenta || row.cuentaCorriente || '-'}
                    </div>
                    <div className='text-[0.705rem] font-medium text-gray-400'>
                      {row.codigoCuenta || row.numeroCuenta || '-'}
                    </div>
                  </td>
                  <td className='max-w-52 px-2 py-2 text-center align-middle'>
                    <div className='font-bold text-[0.65rem]'>
                      {row.descripcion || row.glosa || row.nombreFolio || 'Documento'}
                    </div>
                    <div className='text-[0.705rem] font-medium text-gray-400'>
                      Folio {row.numeroFolio ?? row.folio ?? '-'}
                    </div>
                  </td>
                  <td className='px-2 py-2 text-center align-middle font-bold text-[0.65rem]'>
                    {movementType === 'cargo' ? formatCurrency(amount) : '-'}
                  </td>
                  <td className='px-2 py-2 text-center align-middle font-bold text-[0.65rem]'>
                    {movementType === 'abono' ? formatCurrency(amount) : '-'}
                  </td>
                  <td className='px-2 py-2 text-center align-middle'>
                    <span className={statusBadgeClass(row.status || 'PENDIENTE')}>
                      {normalizeStatus(row.status) || 'PENDIENTE'}
                    </span>
                  </td>
                  <td className='px-2 py-2 text-center align-middle'>
                    <button
                      type='button'
                      onClick={() => onToggle(row, section.key)}
                      className={clsx(
                        'mx-auto flex h-8 w-8 items-center justify-center rounded-full shadow-full-xs transition-colors',
                        checked
                          ? 'bg-accent2 text-white'
                          : 'bg-white text-slate-300 hover:bg-slate-100'
                      )}
                    >
                      <DoneAllIcon sx={{ fontSize: 18 }} />
                    </button>
                    <span className='mt-1 block text-[0.6rem] text-gray-500'>
                      {checked ? 'Seleccionado' : 'Seleccionar'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className='flex h-full min-h-0 flex-col gap-3 rounded-3xl bg-white p-3 shadow-lg'>
      <FiltrosProvisionAgenteAduana
        filters={filters}
        documentOptions={documentOptions}
        onFilterChange={handleFilterChange}
      />

      <div className='min-h-0 flex-1 space-y-3 overflow-auto'>
        {sections.map(section => (
            <section
              key={section.key}
              className='overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm'
            >
              <div className='bg-gray-50 px-4 py-3 text-sm font-medium text-[#000231]'>
                {section.title} ({(selectedIds[section.key] || []).length})
              </div>
              {renderRows(section)}
            </section>
          ))}
      </div>
    </div>
  )
}

export default TablaProvisionAgenteAduana
